import React, { useState, useMemo, useEffect, useRef } from "react";
import { Spin } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlay, faExternalLinkAlt } from "@fortawesome/free-solid-svg-icons";

import styles from "./ResourceDemoPreview.module.css";
import { useGetPublicScenes } from "../../../api/publicCatalog";
import { CONFIG } from "../../../config";

const resolveAssetUrl = (path) => {
  if (!path) return "";
  if (/^https?:\/\//i.test(path)) return path;
  return `${CONFIG.S3_BASE_URI}/${path}`;
};

const resolveSceneImage = (scene) =>
  scene?.image || scene?.thumbnail || scene?.thumbnail_image || "";

export default function ResourceDemoPreview({ projectType }) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [iframeError, setIframeError] = useState(false);
  const iframeRef = useRef(null);

  const { data: publicScenes, isLoading } = useGetPublicScenes();

  const matchingProject = useMemo(() => {
    if (!projectType) return null;
    const list = Array.isArray(publicScenes) ? publicScenes : [];
    return list.find(scene => scene?.type === projectType) || null;
  }, [publicScenes, projectType]);

  // 3. Fallback/Reset
  useEffect(() => {
    // Reset state when projectType changes
    setIsPlaying(false);
    setIframeError(false);
  }, [projectType]);

  // 4. Resolve experience URL
  const experienceUrl = useMemo(() => {
    if (!matchingProject) return "";
    if (projectType === "webUrl") {
      // Use direct URL for AR
      const url = matchingProject.web_url;
      return typeof url === "object" ? url?.url : url;
    }
    // For Unity and 360 Tour, use existing scene-detail flow via ImmersiveLayout
    return `/scene/${matchingProject.public_id}`;
  }, [matchingProject, projectType]);

  // If no match (and done loading), don't render demo box. 
  // Portfolio fallback happens because the DemoCTA exists below this component.
  if (!isLoading && !matchingProject) return null;

  const handlePlay = () => {
    setIsPlaying(true);
  };

  const handleIframeError = () => {
    // In many browsers, this won't fire for X-Frame-Options, but we include it just in case.
    if (projectType === "webUrl") {
      setIframeError(true);
    }
  };

  if (isLoading) {
    return (
      <div className={styles.demoPreviewBox}>
        <div className={styles.demoPreviewLoading}>
          <Spin size="large" />
        </div>
      </div>
    );
  }

  const projectImage = resolveAssetUrl(resolveSceneImage(matchingProject));

  return (
    <div className={styles.demoPreviewBox}>
      {!isPlaying ? (
        <div className={styles.thumbnailState} onClick={handlePlay}>
          {projectImage ? (
            <img
              src={projectImage}
              alt={matchingProject.title || "Experience Preview"}
              className={styles.thumbnailImage}
            />
          ) : (
            <div className={styles.thumbnailPlaceholder} />
          )}
          <div className={styles.playButtonOverlay}>
            <button
              className={styles.playButton}
              aria-label={`Play ${matchingProject.title || "Experience"}`}
            >
              <FontAwesomeIcon icon={faPlay} className={styles.playIcon} />
            </button>
          </div>
        </div>
      ) : (
        <div className={styles.iframeState}>
          {!iframeError ? (
            <>
              {/* Lightweight loading state behind the iframe */}
              <div className={styles.iframeLoadingOverlay}>
                <Spin tip="Loading Experience..." />
              </div>
              <iframe
                ref={iframeRef}
                src={experienceUrl}
                title={matchingProject.title || "Experience Viewer"}
                className={styles.iframeViewer}
                allow="camera *; microphone *; fullscreen *; autoplay *; accelerometer *; gyroscope *; magnetometer *"
                allowFullScreen
                onError={handleIframeError}
                // Optional: Try to detect if we can access the iframe document to see if it's blocked.
                // Cross-origin will throw an error, which we can catch. But it doesn't mean it failed to load, 
                // just that it's cross-origin. So we rely on graceful degradation where possible.
              />
            </>
          ) : (
            <div className={styles.errorFallback}>
              <p>Preview unavailable</p>
              <a
                href={experienceUrl}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.fallbackButton}
              >
                Open Experience <FontAwesomeIcon icon={faExternalLinkAlt} />
              </a>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
